// Sync Service - Đồng bộ dữ liệu với WordPress Server
// Xử lý offline queue, conflict resolution, và background sync

import { StudyProgress, QuizResult, Achievement } from '../../types/learningHistory';
import { sqliteDatabaseService } from '../database/SQLiteDatabaseService';
import { normalizeVatliOrigin } from '../../utils/urlNormalizer';
import { API_CONFIG } from '../../config/api.config';

// ⚠️ TẠM THỜI VÔ HIỆU HÓA SYNC ĐỂ TRÁNH LỖI CORS
const SYNC_DISABLED = true;

interface SyncConfig {
  apiBaseUrl: string;
  syncInterval: number; // milliseconds
  batchSize: number;
  retryAttempts: number;
  retryDelay: number;
}

interface SyncAction {
  id: string;
  type: 'study_progress' | 'quiz_result' | 'achievement';
  action: 'create' | 'update';
  data: any;
  timestamp: number;
  retryCount: number;
  synced: boolean;
}

interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

export class SyncService {
  private static instance: SyncService;
  private config: SyncConfig;
  private syncInterval: NodeJS.Timeout | null = null;
  private isSyncing = false;
  private offlineQueue: SyncAction[] = [];
  private lastSyncTime: number = 0;

  private constructor() {
    // ✅ Use centralized API configuration
    const apiBaseUrl = API_CONFIG.wordpress.apiUrl;

    this.config = {
      apiBaseUrl,
      syncInterval: 30000, // 30 seconds
      batchSize: 50,
      retryAttempts: 3,
      retryDelay: 5000
    };

    console.log(`🔧 SyncService initialized with API: ${apiBaseUrl}`);
    if (SYNC_DISABLED) {
      console.warn('⚠️ SyncService is temporarily DISABLED to prevent CORS errors.');
    }

    // Load offline queue from localStorage
    this.loadOfflineQueue();

    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());

    // Start auto-sync if online
    if (navigator.onLine) {
      this.startAutoSync();
    }
  }

  public static getInstance(): SyncService {
    if (!SyncService.instance) {
      SyncService.instance = new SyncService();
    }
    return SyncService.instance;
  }

  /**
   * Get API Base URL based on environment
   * @deprecated - Now using API_CONFIG
   * 
   * ARCHITECTURE:
   * - Frontend Production: https://ebook.vatli365.vn (or Vercel)
   * - WordPress API: https://vatli365.vn
   * - Local Dev: http://localhost/thi/wordpress
   */
  private getApiBaseUrl(): string {
    // This method is now deprecated, using API_CONFIG instead
    return API_CONFIG.wordpress.apiUrl;
  }

  // ==================== OFFLINE QUEUE MANAGEMENT ====================

  private loadOfflineQueue(): void {
    try {
      const saved = localStorage.getItem('sync_offline_queue');
      if (saved) {
        this.offlineQueue = JSON.parse(saved);
        console.log(`📦 Loaded ${this.offlineQueue.length} items from offline queue`);
      }
    } catch (error) {
      console.error('❌ Failed to load offline queue:', error);
      this.offlineQueue = [];
    }
  }

  private saveOfflineQueue(): void {
    try {
      localStorage.setItem('sync_offline_queue', JSON.stringify(this.offlineQueue));
    } catch (error) {
      console.error('❌ Failed to save offline queue:', error);
    }
  }

  public addToQueue(action: Omit<SyncAction, 'id' | 'timestamp' | 'retryCount' | 'synced'>): void {
    if (SYNC_DISABLED) {
      console.log(`🚫 Sync disabled: Skipping queue add for ${action.type}`);
      return;
    }

    const syncAction: SyncAction = {
      ...action,
      id: this.generateId(),
      timestamp: Date.now(),
      retryCount: 0,
      synced: false
    };

    this.offlineQueue.push(syncAction);
    this.saveOfflineQueue();

    console.log(`📝 Added to sync queue: ${action.type} (${action.action})`);

    // Try to sync immediately if online
    if (navigator.onLine && !this.isSyncing) {
      this.syncQueue();
    }
  }

  private removeFromQueue(actionId: string): void {
    this.offlineQueue = this.offlineQueue.filter(a => a.id !== actionId);
    this.saveOfflineQueue();
  }

  // ==================== SYNC OPERATIONS ====================

  public async syncQueue(): Promise<SyncResult> {
    if (SYNC_DISABLED) {
      return { success: true, synced: 0, failed: 0, errors: [] };
    }

    if (this.isSyncing) {
      console.log('⏳ Sync already in progress, skipping...');
      return { success: false, synced: 0, failed: 0, errors: ['Sync already in progress'] };
    }

    if (!navigator.onLine) {
      console.log('📴 Offline, sync queue postponed');
      return { success: false, synced: 0, failed: 0, errors: ['Device is offline'] };
    }

    if (this.offlineQueue.length === 0) {
      console.log('✅ Sync queue is empty');
      return { success: true, synced: 0, failed: 0, errors: [] };
    }

    this.isSyncing = true;
    console.log(`🔄 Starting sync: ${this.offlineQueue.length} items in queue`);

    let synced = 0;
    let failed = 0;
    const errors: string[] = [];

    // Process queue in batches
    const batches = this.chunkArray(this.offlineQueue, this.config.batchSize);

    for (const batch of batches) {
      for (const action of batch) {
        try {
          await this.syncAction(action);
          this.removeFromQueue(action.id);
          synced++;
          console.log(`✅ Synced: ${action.type} (${action.action})`);
        } catch (error: any) {
          failed++;
          const errorMsg = error.message || 'Unknown error';
          errors.push(`${action.type}: ${errorMsg}`);

          // Increment retry count
          action.retryCount++;

          // Remove if exceeded retry attempts
          if (action.retryCount >= this.config.retryAttempts) {
            console.error(`❌ Max retries exceeded for ${action.type}, removing from queue`);
            this.removeFromQueue(action.id);
          } else {
            console.warn(`⚠️ Retry ${action.retryCount}/${this.config.retryAttempts} for ${action.type}`);
            this.saveOfflineQueue();
          }
        }
      }
    }

    this.isSyncing = false;
    this.lastSyncTime = Date.now();

    console.log(`🎉 Sync completed: ${synced} synced, ${failed} failed`);

    return {
      success: failed === 0,
      synced,
      failed,
      errors
    };
  }

  private async syncAction(action: SyncAction): Promise<void> {
    if (SYNC_DISABLED) return;

    switch (action.type) {
      case 'study_progress':
        return action.action === 'create'
          ? this.syncStudyProgress(action.data)
          : this.updateStudyProgress(action.data);

      case 'quiz_result':
        return this.syncQuizResult(action.data);

      case 'achievement':
        return this.syncAchievement(action.data);

      default:
        throw new Error(`Unknown action type: ${action.type}`);
    }
  }

  // ==================== SYNC INDIVIDUAL ITEMS ====================

  private async syncStudyProgress(progress: StudyProgress): Promise<void> {
    if (SYNC_DISABLED) return;

    // ✅ Validate required fields before making request
    if (!progress.userId || !progress.lessonId) {
      throw new Error('Missing required fields: userId and lessonId are required');
    }

    try {
      const response = await fetch(`${this.config.apiBaseUrl}/wp-json/qoder/v1/study-progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: progress.userId,
          lessonId: progress.lessonId,
          lessonTitle: progress.lessonTitle,
          progress: progress.progress,
          timeSpent: progress.timeSpent,
          status: progress.status,
          startedAt: progress.startedAt,
          completedAt: progress.completedAt,
          lastAccessedAt: progress.lastAccessedAt,
          bookmarks: progress.bookmarks,
          notes: progress.notes
        })
      });

      // ✅ Handle specific error status codes
      if (response.status === 400) {
        let errorMessage = 'Bad request - invalid data format';
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch (e) {
          // Response is not JSON
        }
        throw new Error(errorMessage);
      }

      if (response.status === 401) {
        throw new Error('Authentication required - please login again');
      }

      if (!response.ok) {
        let errorMessage = 'Failed to sync study progress';
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch (e) {
          errorMessage = `${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      // ✅ Re-throw with more context
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Failed to sync study progress: ${error.message || 'Unknown error'}`);
    }
  }

  private async updateStudyProgress(progress: StudyProgress): Promise<void> {
    if (SYNC_DISABLED) return;

    // ✅ Validate progress ID before making request
    if (!progress.id || progress.id.trim() === '') {
      console.warn('⚠️ [updateStudyProgress] Invalid progress ID, attempting to create new record instead');
      // Try to create new record if ID is invalid
      return this.syncStudyProgress(progress);
    }

    try {
      const response = await fetch(`${this.config.apiBaseUrl}/wp-json/qoder/v1/study-progress/${progress.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: progress.userId,
          progress: progress.progress,
          timeSpent: progress.timeSpent,
          status: progress.status,
          completedAt: progress.completedAt,
          lastAccessedAt: progress.lastAccessedAt,
          bookmarks: progress.bookmarks,
          notes: progress.notes
        })
      });

      // ✅ Handle 400 Bad Request - ID may not exist, try creating new record
      if (response.status === 400) {
        console.warn(`⚠️ [updateStudyProgress] Bad request for ID ${progress.id}, attempting to create new record`);
        // Try to create new record instead
        return this.syncStudyProgress(progress);
      }

      // ✅ Handle 404 Not Found - ID doesn't exist, create new record
      if (response.status === 404) {
        console.warn(`⚠️ [updateStudyProgress] Progress ID ${progress.id} not found, creating new record`);
        return this.syncStudyProgress(progress);
      }

      if (!response.ok) {
        let errorMessage = 'Failed to update study progress';
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = `${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      // ✅ If update fails, try to create new record as fallback
      if (error.message?.includes('400') || error.message?.includes('404')) {
        console.warn('⚠️ [updateStudyProgress] Update failed, attempting to create new record');
        try {
          return await this.syncStudyProgress(progress);
        } catch (createError) {
          throw new Error(`Failed to update or create study progress: ${createError instanceof Error ? createError.message : 'Unknown error'}`);
        }
      }
      throw error;
    }
  }

  private async syncQuizResult(result: QuizResult): Promise<void> {
    if (SYNC_DISABLED) return;

    const response = await fetch(`${this.config.apiBaseUrl}/wp-json/qoder/v1/quiz-results`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: result.userId,
        quizId: result.quizId,
        lessonId: result.lessonId,
        quizTitle: result.quizTitle,
        quizType: result.quizType,
        score: result.score,
        totalQuestions: result.totalQuestions,
        correctAnswers: result.correctAnswers,
        wrongAnswers: result.wrongAnswers,
        skippedAnswers: result.skippedAnswers,
        timeSpent: result.timeSpent,
        passed: result.passed,
        attemptNumber: result.attemptNumber,
        answers: result.answers
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to sync quiz result');
    }
  }

  private async syncAchievement(achievement: Achievement): Promise<void> {
    if (SYNC_DISABLED) return;

    const response = await fetch(`${this.config.apiBaseUrl}/wp-json/qoder/v1/achievements`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: achievement.userId,
        achievementType: achievement.achievementType,
        title: achievement.title,
        description: achievement.description,
        icon: achievement.icon,
        earnedAt: achievement.earnedAt,
        metadata: achievement.metadata
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to sync achievement');
    }
  }

  // ==================== PULL FROM SERVER ====================

  public async pullFromServer(userId: string): Promise<void> {
    if (SYNC_DISABLED) {
      console.log('🚫 Sync disabled: Skipping pull from server');
      return;
    }

    if (!navigator.onLine) {
      console.log('📴 Offline, cannot pull from server');
      return;
    }

    console.log('⬇️ Pulling data from server...');

    try {
      // Pull study progress
      const progressResponse = await fetch(
        `${this.config.apiBaseUrl}/wp-json/qoder/v1/study-progress?userId=${userId}`
      );

      if (progressResponse.ok) {
        const serverProgress: StudyProgress[] = await progressResponse.json();
        await this.mergeStudyProgress(userId, serverProgress);
      }

      // Pull quiz results
      const quizResponse = await fetch(
        `${this.config.apiBaseUrl}/wp-json/qoder/v1/quiz-results?userId=${userId}`
      );

      if (quizResponse.ok) {
        const serverQuizzes: QuizResult[] = await quizResponse.json();
        await this.mergeQuizResults(userId, serverQuizzes);
      }

      // Pull achievements
      const achievementsResponse = await fetch(
        `${this.config.apiBaseUrl}/wp-json/qoder/v1/achievements?userId=${userId}`
      );

      if (achievementsResponse.ok) {
        const serverAchievements: Achievement[] = await achievementsResponse.json();
        await this.mergeAchievements(userId, serverAchievements);
      }

      console.log('✅ Pull from server completed');
    } catch (error) {
      console.error('❌ Failed to pull from server:', error);
    }
  }

  // ==================== MERGE STRATEGIES (SERVER WINS) ====================

  private async mergeStudyProgress(userId: string, serverData: StudyProgress[]): Promise<void> {
    for (const serverItem of serverData) {
      const localItem = await sqliteDatabaseService.getStudyProgress(userId, serverItem.lessonId);

      if (!localItem) {
        // Server has data, local doesn't → Create local
        await sqliteDatabaseService.createStudyProgress(serverItem);
        console.log(`📥 Created local: ${serverItem.lessonId}`);
      } else {
        // Both have data → Server wins (overwrite local)
        await sqliteDatabaseService.updateStudyProgress(userId, serverItem.lessonId, {
          progress: serverItem.progress,
          timeSpent: serverItem.timeSpent,
          status: serverItem.status,
          completedAt: serverItem.completedAt,
          lastAccessedAt: serverItem.lastAccessedAt,
          bookmarks: serverItem.bookmarks,
          notes: serverItem.notes
        });
        console.log(`🔄 Updated local from server: ${serverItem.lessonId}`);
      }
    }
  }

  private async mergeQuizResults(userId: string, serverData: QuizResult[]): Promise<void> {
    for (const serverItem of serverData) {
      // Quiz results are append-only, just add if not exists locally
      const localResults = await sqliteDatabaseService.getQuizResults(userId);
      const exists = localResults.some(r =>
        r.quizId === serverItem.quizId &&
        r.attemptNumber === serverItem.attemptNumber
      );

      if (!exists) {
        await sqliteDatabaseService.saveQuizResult(serverItem);
        console.log(`📥 Created local quiz result: ${serverItem.quizId} #${serverItem.attemptNumber}`);
      }
    }
  }

  private async mergeAchievements(userId: string, serverData: Achievement[]): Promise<void> {
    for (const serverItem of serverData) {
      const hasLocal = await sqliteDatabaseService.hasAchievement(userId, serverItem.achievementType);

      if (!hasLocal) {
        await sqliteDatabaseService.createAchievement(serverItem);
        console.log(`📥 Created local achievement: ${serverItem.achievementType}`);
      }
    }
  }

  // ==================== AUTO SYNC ====================

  private startAutoSync(): void {
    if (SYNC_DISABLED) return;

    if (this.syncInterval) return;

    console.log('🔄 Auto-sync started');

    this.syncInterval = setInterval(() => {
      this.syncQueue();
    }, this.config.syncInterval);
  }

  private stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
      console.log('🛑 Auto-sync stopped');
    }
  }

  // ==================== NETWORK EVENT HANDLERS ====================

  private handleOnline(): void {
    console.log('🌐 Device is online');
    this.startAutoSync();
    this.syncQueue(); // Sync immediately when coming online
  }

  private handleOffline(): void {
    console.log('📴 Device is offline');
    this.stopAutoSync();
  }

  // ==================== UTILITY METHODS ====================

  private chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  private generateId(): string {
    return `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  public getQueueSize(): number {
    return this.offlineQueue.length;
  }

  public getLastSyncTime(): number {
    return this.lastSyncTime;
  }

  public isOnline(): boolean {
    return navigator.onLine;
  }

  public isSyncInProgress(): boolean {
    return this.isSyncing;
  }

  public getConfig(): SyncConfig {
    return { ...this.config };
  }
}

// Export singleton instance
export const syncService = SyncService.getInstance();
